import { useMemo } from 'react';
import { StanBanner, StanLink } from '@/components';
import { useUser } from '@/queries/useUser';
import { hasActiveSubscription } from '@/features/subscriptions/logic/subscriptionStatus.logic';
import { useStripeSetupBanner } from '@/hooks/useStripeSetupBanner';
import { useGlobalStore } from '@/stores';

/**
 * LayoutSubscriptionBanner
 *
 * Displays subscription and account status banners at the layout level.
 * Shows warnings for:
 * - Deactivated accounts
 * - Inactive subscriptions
 * - Missing Stripe/direct deposit setup
 *
 * This component is designed to be used within MainLayout and handles
 * all the logic for determining which banners to display.
 */
export function LayoutSubscriptionBanner() {
    const { user } = useUser();
    const { showStripeSetupBanner } = useStripeSetupBanner();
    const showProductEditorNavigation = useGlobalStore(state => state.showProductEditorNavigation);

    const isAccountDisabled = user?.status === '0';

    // Calculate if we should show the top banner box
    const showTopBannerBox = useMemo(() => {
        if (!user) return false;
        const hasActiveSubscriptionValue = hasActiveSubscription(user);
        return isAccountDisabled || !hasActiveSubscriptionValue || showStripeSetupBanner;
    }, [user, isAccountDisabled, showStripeSetupBanner]);

    // Don't render if banners shouldn't be shown or product editor navigation is active
    if (!showTopBannerBox || showProductEditorNavigation) {
        return null;
    }

    return (
        <div className='mb-4 flex flex-col'>
            {isAccountDisabled && (
                <StanBanner variant='warning' className='mb-2.5'>
                    <strong>Your account has been deactivated.</strong> To reactivate your account, please contact{' '}
                    <a href='mailto:friends@stanwith.me' className='underline'>
                        friends@stanwith.me
                    </a>
                    .
                </StanBanner>
            )}
            {user && !hasActiveSubscription(user) && (
                <StanBanner variant='warning' className='mb-2.5'>
                    Your store isn&apos;t live yet. No worries! You can renew your subscription{' '}
                    <StanLink to='/subscribe' className='underline'>
                        here
                    </StanLink>
                    .
                </StanBanner>
            )}
            {showStripeSetupBanner && (
                <StanBanner variant='warning' className='mb-2.5'>
                    Heads up, customers can&apos;t purchase from you yet! Please{' '}
                    <StanLink to='/account_settings?tab=store' className='underline'>
                        set up your Direct Deposit
                    </StanLink>{' '}
                    to start selling
                </StanBanner>
            )}
        </div>
    );
}
